import { createFileRoute, redirect } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/atoms/card';
import { PortalShell } from '@/components/portal/PortalShell';
import { PortalSingleScopeEstimate } from '@/components/portal/PortalSingleScopeEstimate';
import { usePortalEstimate } from '@/hooks/portal/usePortalEstimates';
import { ApiError } from '@/lib/api/errors';
import { hasLivePortalSession } from '@/stores/portalAuth.store';

const DEPOSIT_SESSION_RE = /^cs_(test|live)_[a-zA-Z0-9]+$/;

export const Route = createFileRoute('/(portal)/portal/estimates/$id')({
  // Same validation the public quote page uses so a tampered return URL can't
  // drive the settlement flow with arbitrary input. Both keys are OPTIONAL so
  // ordinary navigation to the estimate needs no search params (only the Stripe
  // deposit round-trip carries them).
  validateSearch: (
    search: Record<string, unknown>,
  ): { deposit?: 'success' | 'cancel'; session_id?: string } => ({
    deposit:
      search.deposit === 'success' || search.deposit === 'cancel'
        ? (search.deposit as 'success' | 'cancel')
        : undefined,
    session_id:
      typeof search.session_id === 'string' && DEPOSIT_SESSION_RE.test(search.session_id)
        ? search.session_id
        : undefined,
  }),
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalEstimateDetailPage,
});

export function PortalEstimateDetailPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const estimate = usePortalEstimate(id, search.deposit);
  const notFound = estimate.error instanceof ApiError && estimate.error.status === 404;

  // Portal estimates always use Single Scope. Public links and PDFs retain
  // their configured design; only this portal presentation is fixed.
  return (
    <PortalShell documentView back={{ label: 'Estimates', to: '/portal/estimates' }}>
      {estimate.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : notFound ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            This estimate isn&apos;t available.
          </CardContent>
        </Card>
      ) : estimate.isError ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            We couldn&apos;t load this estimate. Please try again.
          </CardContent>
        </Card>
      ) : (
        <PortalSingleScopeEstimate
          key={id}
          data={estimate.data}
          id={id}
          deposit={search.deposit}
          sessionId={search.session_id}
        />
      )}
    </PortalShell>
  );
}
